<template>
  <section class="reviews-block">
    <div class="container">
      <div class="reviews-block__head">
        <h2 class="h2">{{ values.heading }}</h2>
        <nuxt-link
          v-if="values.link"
          :to="values.link"
          class="--link"
          prefetch
        >
          {{ values.link_title }}
          <nuxt-img
            provider="localProvider"
            src="/assets/img/svg/common/arrow.svg"
            alt="стрелка"
            width="20"
            height="20"
            loading="lazy"
            decoding="async"
            fetchpriority="low"
          />
        </nuxt-link>
      </div>

      <div
        v-if="descriptionFromValues"
        v-html="descriptionFromValues"
        class="reviews-block__description"
      />

      <!-- Skeleton loader для предотвращения CLS -->
      <template v-if="pending">
        <div class="reviews-block__skeleton">
          <div 
            v-for="i in 3" 
            :key="`skeleton-${i}`"
            class="reviews-block__skeleton-item"
          />
        </div>
      </template>

      <!-- Desktop view -->
      <template v-else-if="isDesktop">
        <div 
          class="reviews-block__items"
          :style="{ contentVisibility: 'auto' }"
        >
          <template
            v-for="(_item) in reviews"
            :key="`reviews-${_item.id}`"
          >
            <ReviewCard
              :is-manager="isManager"
              :item="_item"
            />
          </template>
        </div>
      </template>
      
      <!-- Mobile view -->
      <template v-else>
        <ClientOnly>
          <!-- Показываем Swiper только если компонент загружен -->
          <component
            v-if="MobileSwiper"
            :is="MobileSwiper"
            :is-manager="isManager"
            :reviews="reviews"
          />
          <!-- Fallback пока компонент загружается -->
          <div 
            v-else
            class="reviews-block__fallback"
          >
            <div 
              v-for="i in Math.min(reviews?.length || 0, 2)" 
              :key="`fallback-${i}`"
              class="fallback-item"
            />
          </div>
          
          <template #fallback>
            <!-- Fallback для SSR -->
            <div class="reviews-block__fallback">
              <div 
                v-for="i in Math.min(reviews?.length || 0, 2)" 
                :key="`fallback-ssr-${i}`"
                class="fallback-item"
              />
            </div>
          </template>
        </ClientOnly>
        <nuxt-link
          v-if="values.link"
          :to="values.link"
          class="btn btn-outline reviews-block__button-mobile"
          prefetch
        >
          {{ values.link_title }}
        </nuxt-link>
      </template>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { computed, watch } from 'vue';
import { useNuxtApp } from 'nuxt/app';
import type { IBlockDefaultProps } from '#sitis/internal/models/common';
import useSitisUserManager from '~/composables/useSitisUserManager';

// Lazy loading компонентов для оптимизации bundle size
const ReviewCard = defineAsyncComponent(() => import('~/components/reviews/ReviewCard.vue'));

const props = defineProps<IBlockDefaultProps>();
const { $api, $viewport } = useNuxtApp();

const { userManagerInfo } = useSitisUserManager();

// Динамический импорт MobileSwiper только на мобильных устройствах
const MobileSwiper = ref<any>(null);
const loadMobileSwiper = async () => {
  if (!MobileSwiper.value && !$viewport.isGreaterOrEquals('lg')) {
    const component = await import('~/components/reviews/MobileSwiper.vue');
    MobileSwiper.value = component.default;
  }
};

// SSR-совместимая загрузка данных с улучшенным кэшированием
// Использует useAsyncData для работы на сервере и клиенте
const { data: reviews, pending } = await useAsyncData(
  'reviews-block-data', // Уникальный ключ для кэширования
  async () => {
    try {
      const res = await $api.agent.get('/reviews?per-page=3&filter[type]=review&expand=files&sort=rnd');
      return res._data || [];
    } catch (error) {
      console.error('Failed to load reviews:', error);
      return [];
    }
  },
  {
    // Кэшировать данные для повторного использования
    lazy: false, // Загружаем сразу для SSR
    server: true, // Выполнять на сервере
    dedupe: 'defer', // Дедупликация запросов
    getCachedData(key) {
      const nuxtApp = useNuxtApp();
      const data = nuxtApp.payload.data[key] || nuxtApp.static.data[key];
      
      // Проверяем свежесть данных (кэш на 5 минут)
      if (!process.server && data) {
        const cachedTime = nuxtApp.payload.data[`${key}_time`];
        if (cachedTime && Date.now() - cachedTime < 5 * 60 * 1000) {
          return data;
        }
      }
      
      return data;
    }
  }
);

// Мемоизация проверки на менеджера
const isManager = computed(() => Boolean(userManagerInfo.value));

// Кэшированный viewport check для минимизации перерисовок
const isDesktop = computed(() => $viewport.isGreaterOrEquals('lg'));

// Оптимизированная логика выбора описания
const descriptionFromValues = computed(() => {
  return isDesktop.value 
    ? props.values.description_desktop 
    : props.values.description_mobile;
});

// Загружаем MobileSwiper только когда переключаемся на мобильную версию
watch(isDesktop, (newValue) => {
  if (!newValue && !pending.value) {
    loadMobileSwiper();
  }
}, { immediate: true });

function buildReviewsJsonLd(list: any[]) {
  if (!list?.length) return null;
  const strip = (s: string) => (typeof s === 'string' ? s : '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  const fmt = (ts: number | undefined) => {
    if (ts == null || Number.isNaN(Number(ts))) return '';
    const d = new Date(Number(ts) * 1000);
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
  };
  return {
    '@context': 'https://schema.org',
    '@graph': list.map((item: any) => {
      const plain = strip(item.text || '');
      return {
        '@type': 'Review',
        reviewRating: { '@type': 'Rating', worstRating: 0, bestRating: 5, ratingValue: Number(item.rating) || 0 },
        itemReviewed: { '@type': 'Thing', name: plain.slice(0, 35) },
        author: { '@type': 'Person', name: item.name || '' },
        datePublished: fmt(item.created_at),
        reviewBody: plain
      };
    })
  };
}

const reviewsJsonLd = computed(() => buildReviewsJsonLd(reviews.value || []));

useHead(computed(() => {
  const data = reviewsJsonLd.value;
  if (!data) return {};
  return {
    script: [{ type: 'application/ld+json', innerHTML: JSON.stringify(data), key: 'reviews-block-jsonld' }]
  };
}));

</script>

<style lang="scss" scoped>
.reviews-block {
	padding: 40px 0;
  margin-top: 14px;
	box-sizing: border-box;
	background-color: white;
	border-radius: 30px 30px 0 0;
	position: relative;
	z-index: 1;
}

.reviews-block__head {
	display: flex;
	align-items: center;
	margin-bottom: 32px;

	.--link {
		display: flex;
		align-items: center;
		margin-left: auto;
		color: #000;
		font-variant-numeric: lining-nums proportional-nums;
		font-size: 16px;
		font-weight: 500;
		line-height: 24px;

		img {
			transform: rotate(180deg);
			width: 20px;
			height: 20px;
			margin-left: 6px;
		}

		&:hover {
			text-decoration: underline;
		}
	}
}

.reviews-block__description {
  font-weight: 500;
  line-height: 130%;
  padding-bottom: 32px;
}

.reviews-block__items {
	display: flex;
	flex-wrap: wrap;
	margin-top: -32px;
	margin-left: -32px;

	& > * {
		width: calc(100% / 3 - 32px);
		margin-top: 32px;
		margin-left: 32px;
	}
}

.reviews-block__button-mobile {
	border-color: #404040;
	margin-top: 18px;
	height: 48px;
}

/* Skeleton loader для предотвращения CLS */
.reviews-block__skeleton {
	display: flex;
	flex-wrap: wrap;
	margin-top: -32px;
	margin-left: -32px;
	animation: pulse 1.5s ease-in-out infinite;
}

.reviews-block__skeleton-item {
	width: calc(100% / 3 - 32px);
	height: 380px;
	margin-top: 32px;
	margin-left: 32px;
	border-radius: 20px;
	background: linear-gradient(
		90deg,
		#f4f3f1 25%,
		#ebebeb 50%,
		#f4f3f1 75%
	);
	background-size: 200% 100%;
	animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
	0% {
		background-position: 200% 0;
	}
	100% {
		background-position: -200% 0;
	}
}

// Fallback для SSR на мобильных
.reviews-block__fallback {
	display: flex;
	gap: 16px;
	padding: 0 20px;
	margin: 0 -20px;
	overflow: hidden;
}

.fallback-item {
	min-width: calc(100% - 40px);
	height: 350px;
	border-radius: 20px;
	background: #f4f3f1;
	
	@media (min-width: 640px) {
		min-width: calc(50% - 28px);
	}
}

// Performance optimization для больших списков
.reviews-block__items {
	contain: layout style paint;
	
	& > * {
		content-visibility: auto;
		contain-intrinsic-size: auto 380px;
	}
}

@media (max-width: 1359px) {
  .reviews-block__skeleton {
    margin-top: -20px;
    margin-left: -20px;
  }
  .reviews-block__skeleton-item {
    width: calc(100% / 3 - 20px);
    margin-top: 20px;
    margin-left: 20px;
  }
  .reviews-block__items {
    margin-top: -20px;
    margin-left: -20px;
    & > * {
      width: calc(100% / 3 - 20px);
      margin-top: 20px;
      margin-left: 20px;
    }
  }
}
@media (max-width: 1023px) {
	.reviews-block {
		padding: 40px 0;
	}
	.reviews-block__head {
		.--link {
			display: none;
		}
	}
	.reviews-block__skeleton {
		margin: 0;
		display: block;
	}
	.reviews-block__skeleton-item {
		width: 100%;
		height: 350px;
		margin: 0 0 16px 0;
	}
}
</style>
